<template>
  <div class="your-pets-page" :style="{ backgroundImage: `url(${designUrl}MyPets.jpeg)` }">
    <div class="sidebar-wrapper">
      <button class="hamburger" @click="sidebarOpen = !sidebarOpen">☰</button>
      <div class="sidebar" :class="{ open: sidebarOpen }">
        <div class="sidebar-header">
          <button class="close-btn" @click="sidebarOpen = false">✖</button>
          <p>{{ user.fullname }}</p>
          <p>{{ user.phone_number }}</p>
        </div>
        <ul class="sidebar-menu">

          <template v-if="user && user.user_type === 'Pet Owner'">
            <li @click="goToPage('DashboardPage')">Browse Pets</li>
            <li class="has-submenu" @click="petsOpen = !petsOpen">
              My Pets
              <span class="arrow" :class="{ 'arrow-open': petsOpen }">›</span>
            </li>
            <ul class="sub-menu" :class="{ 'sub-open': petsOpen }">
              <li @click="goToPage('YourPetsPage')">View My Pets</li>
              <li @click="goToPage('AddPetPage')">Add Pet</li>
              <li @click="goToPage('MeetingsPage')">Booking Requests</li>
            </ul>
            <li @click="goToPage('EditUserDashboard')">My Profile</li>
            <li @click="goToPage('LogoutPage')">Log Out</li>
          </template>

          <template v-else>
            <li @click="goToPage('DashboardPage')">Browse Pets</li>
            <li @click="goToPage('UsersFavouritesPage')">My Favourites</li>
            <li @click="goToPage('CalendarPage')">My Calendar</li>
            <li @click="goToPage('EditUserDashboard')">My Profile</li>
            <li @click="goToPage('LogoutPage')">Log Out</li>
          </template>

        </ul>
      </div>
    </div>

    <div class="page-header">
      <h1 class="page-title">My Pets</h1>
      <p class="page-sub">Manage your pet profiles</p>
    </div>

    <div class="page-wrapper">

      <div v-if="myPets.length === 0" class="empty-state">
        <div class="empty-icon">🐾</div>
        <p class="empty-text">No pets yet</p>
        <p class="empty-sub">Add your first pet to get started</p>
      </div>

      <div v-else class="pets-grid">
        <div v-for="pet in myPets" :key="pet.id" class="pet-card">

          <div class="card-img-wrap">
            <img
              v-if="pet.pet_photo"
              :src="backendUrl + '/pet-pics/' + pet.pet_photo"
              :alt="pet.pet_name"
              class="card-img"
            />
            <div class="online-dot" :class="pet.isOnline ? 'dot-online' : 'dot-offline'"></div>
          </div>

          <div class="card-body">
            <p class="pet-name">{{ pet.pet_name }}</p>
            <p class="pet-breed">{{ pet.breed }}</p>

            <div class="badges">
              <span class="badge badge-age">{{ pet.age }}</span>
              <span class="badge" :class="pet.gender === 'Male' ? 'badge-male' : 'badge-female'">
                {{ pet.gender }}
              </span>
            </div>

            <p v-if="pet.location" class="pet-location">
              📍 {{ formatLocation(pet.location) }}
            </p>

            <p class="pet-description">{{ pet.description }}</p>

            <div class="card-actions">
              <button
                class="action-btn toggle-btn"
                :class="pet.isOnline ? 'btn-offline' : 'btn-online'"
                @click="togglePetStatus(pet)"
              >
                {{ pet.isOnline ? 'Set Offline' : 'Set Online' }}
              </button>
              <button class="action-btn edit-btn" @click="$emit('edit-pet', pet)">
                Edit
              </button>
            </div>
          </div>

        </div>
      </div>

    </div>
  </div>
</template>

<script>
import axios from 'axios';
const backendUrl = process.env.VUE_APP_API_URL;

export default {
  props: {
    user: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      myPets: [],
      backendUrl,
      designUrl: '/background_designs/',
      sidebarOpen: false,
      petsOpen: false,
    };
  },
  async mounted() {
    try {
      const { data } = await axios.get(
        `${backendUrl}/api/pets/my-pets`,
        { withCredentials: true }
      );
      this.myPets = data;
    } catch (err) {
      console.error("Error loading data:", err);
    }
  },
  methods: {
    async togglePetStatus(pet) {
      const newIsOnline = !pet.isOnline;
      await axios.patch(
        `${backendUrl}/api/pets/${pet.id}/visibility`,
        { isOnline: newIsOnline },
        { withCredentials: true }
      );
      pet.isOnline = newIsOnline;
    },
    openEditPetDashboard(pet) {
      this.$emit('edit-pet', pet);
    },
    formatLocation(location) {
      try {
        const loc = typeof location === "string" ? location : JSON.stringify(location);
        const parts = loc.split(",").map(p => p.trim());
        if (parts.length >= 2) {
          return parts.slice(-3, -1).join(", ") || parts.slice(0, 2).join(", ");
        }
        return loc;
      } catch {
        return location;
      }
    },
    goToPage(pageName) {
      this.sidebarOpen = false;
      this.$emit('change-page', pageName);
    },
  }
};
</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.your-pets-page {
  min-height: 100vh;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

/* ── Page header ── */
.page-header {
  max-width: 1200px;
  margin: 0 auto;
  padding: 60px 20px 0 24px;
}

.page-title {
  font-size: 26px;
  font-weight: 800;
  color: #1a1a1a;
  letter-spacing: -0.4px;
  margin: 0;
}

.page-sub {
  font-size: 14px;
  color: #aaa;
  font-weight: 500;
  margin-top: 4px;
}

/* ── Page wrapper ── */
.page-wrapper {
  max-width: 1000px;
  margin: 0 auto;
  padding: 24px 20px 60px;
}

/* ── Grid ── */
.pets-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 20px;
}

/* ── Card ── */
.pet-card {
  background: #ffffff;
  border-radius: 24px;
  border: 1px solid #f0e0ff;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 16px rgba(186, 113, 255, 0.07);
  transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.25s ease;
}

.pet-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 16px 36px rgba(186, 113, 255, 0.13);
}

.card-img-wrap {
  position: relative;
  width: 100%;
  height: 170px;
  background: #f7f3ee;
  overflow: hidden;
}

.card-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.4s ease;
}

.pet-card:hover .card-img { transform: scale(1.05); }

.online-dot {
  position: absolute;
  top: 12px;
  left: 13px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: 2.5px solid #fff;
}

.dot-online  { background: #22c55e; box-shadow: 0 0 0 3px rgba(34,197,94,0.2); }
.dot-offline { background: #d1d5db; }

.card-body {
  padding: 15px 16px 17px;
  display: flex;
  flex-direction: column;
  gap: 5px;
  flex: 1;
}

.pet-name {
  font-size: 16px;
  font-weight: 800;
  color: #1a1a1a;
  margin: 0;
  letter-spacing: -0.2px;
}

.pet-breed {
  font-size: 12px;
  color: #aaa;
  font-weight: 600;
  margin: 0;
}

.badges { display: flex; gap: 5px; flex-wrap: wrap; margin-top: 4px; }

.badge {
  font-size: 11px;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 999px;
}

.badge-age    { background: #f3eeff; color: #6d28d9; }
.badge-male   { background: #eff6ff; color: #1d4ed8; }
.badge-female { background: #fdf2f8; color: #be185d; }

.pet-location {
  font-size: 12px;
  color: #aaa;
  font-weight: 600;
  margin: 2px 0 0;
}

.pet-description {
  font-size: 12px;
  color: #999;
  line-height: 1.5;
  margin-top: 4px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.card-actions {
  display: flex;
  gap: 8px;
  margin-top: auto;
  padding-top: 12px;
}

.action-btn {
  flex: 1;
  padding: 9px 0;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 700;
  border: none;
  cursor: pointer;
  transition: background 0.18s, transform 0.1s;
  letter-spacing: 0.01em;
}

.action-btn:active { transform: scale(0.97); }

.btn-online  { background: #d4f5e2; color: #1a6b3c; }
.btn-online:hover  { background: #a8edca; }
.btn-offline { background: #fde8e8; color: #991b1b; }
.btn-offline:hover { background: #fbc8c8; }

.edit-btn { background: #eac7ff; color: #4a1070; }
.edit-btn:hover { background: #ba71ff; color: #fff; }

/* ── Empty state ── */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  gap: 10px;
}

.empty-icon { font-size: 48px; opacity: 0.5; }
.empty-text { font-size: 17px; font-weight: 700; color: #333; margin: 0; }
.empty-sub  { font-size: 13px; color: #bbb; margin: 0; }

/* ── Responsive ── */
@media (max-width: 700px) {
  .pets-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }
  .card-img-wrap { height: 140px; }
  .pet-name { font-size: 14px; }
}

@media (max-width: 400px) {
  .pets-grid { grid-template-columns: 1fr; }
}
</style>